import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Component, computed, DestroyRef, inject, signal, viewChild } from '@angular/core';
import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  buildWizardStepDependencyMeta,
  persistWizardStepData,
  DEFAULT_TOASTER_LIFE,
  IWizardContext,
  ModalBuilder,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnButton,
  ZnSelectQueryParams,
  ZnSingleSelect,
} from '@zendra/shared';
import { firstValueFrom, of } from 'rxjs';
import type {
  StandDivisionAndFusionFusedStandAdvanceValidationInputDto,
  StandDivisionAndFusionFusedStandDetailDto,
  StandDivisionAndFusionFusedStandValidationInputDto,
} from 'projects/orchestrator/src/lib/proxy/orchestrator/dtos/stand-division-and-fusion/models';
import { StandCreationForm } from '../../stand/stand-creation-form/stand-creation-form';
import { WRAPPER_CONFIG } from '../../stand/stand.config';
import { IDividedStandRow } from '../interfaces/IDividedStandRow';
import { ISelectStandsToDivideStepData } from '../interfaces/ISelectStandsToDivideStepData';
import { StandDivisionAndFusion } from '../services/stand-division-and-fusion';
import { DivisionFusionStand } from '../stand-division-and-fusion.data';
import { extractBackendErrorMessage, isHandledByZnErrorHandler } from '../utils/backend-error';

type SelectValue = string | string[] | null;

export interface StepSelectFusedStandData {
  sourceStep2Snapshot: string;
  propertyId: string | null;
  selectedStands: IDividedStandRow[];
  fusedStand: DivisionFusionStand | null;
  fusedStandDetail: StandDivisionAndFusionFusedStandDetailDto | null;
}

const EMPTY_VALUE = '-----';

@Component({
  selector: 'lib-step-select-fused-stand',
  standalone: true,
  imports: [LocalizationPipe, ZnButton, ZnSingleSelect],
  templateUrl: './step-select-fused-stand.html',
})
export class StepSelectFusedStand
  implements WizardStepComponent<StepSelectFusedStandData>
{
  readonly standSelect = viewChild('standSelect', { read: ZnSingleSelect });

  private readonly toaster = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalBuilder = inject(ModalBuilder);
  private readonly standDivisionAndFusionService = inject(StandDivisionAndFusion);

  readonly localizationKeyPrefix = 'Geographic::StandDivisionAndFusion.StepSelectFusedStand';
  readonly localizationResource = ZendraLocalizationServices.LanguageService;
  readonly previousStepId = 2;

  readonly propertyId = signal<string | null>(null);
  readonly selectedStands = signal<IDividedStandRow[]>([]);
  readonly referenceSpecies = signal<string | null>(null);
  readonly selectedStandId = signal<string | null>(null);
  readonly selectedStandDetail = signal<StandDivisionAndFusionFusedStandDetailDto | null>(null);
  readonly loading = signal(false);
  readonly refreshVersion = signal(0);

  readonly fusedStandOptions = computed(() => {
    const detail = this.selectedStandDetail();
    if (detail?.id === undefined || detail.id === null) {
      return [];
    }

    const standId = String(detail.id);
    return [{ key: standId, value: detail.name ?? standId }];
  });
  readonly loadFusedStands = computed(() => {
    this.refreshVersion();

    return (params?: ZnSelectQueryParams) => {
      const propertyId = this.numericPropertyId();
      if (!propertyId) {
        return of({ items: [], totalCount: 0 });
      }

      return this.standDivisionAndFusionService.searchFusedStandOptions(propertyId, params);
    };
  });
  readonly fusedStand = computed(() => this.toFusedStand(this.selectedStandDetail()));
  readonly selectionSectionClass = computed(() =>
    this.fusedStand()
      ? 'zn-step-content-section'
      : 'zn-step-content-section zn-step-content-section--selected',
  );
  readonly summarySectionClass = computed(() =>
    this.fusedStand()
      ? 'zn-full-height zn-step-content-section stand-division-description-section zn-step-content-section--selected'
      : 'zn-full-height zn-step-content-section stand-division-description-section zn-step-content-section--disabled',
  );

  onActivate(context: IWizardContext): void {
    const activeStepId = context.activeStepId();
    const previousStepData =
      context.getStepState<ISelectStandsToDivideStepData>(this.previousStepId)?.data;
    const currentState =
      context.getStepState<StepSelectFusedStandData>(context.activeStepId())?.data;
    const sourceStep2Snapshot = this.buildStep2Snapshot(previousStepData);
    const shouldRestoreCurrentData =
      !!currentState && currentState.sourceStep2Snapshot === sourceStep2Snapshot;

    context.patchStepState(activeStepId, {
      visited: true,
    });

    this.propertyId.set(
      previousStepData?.propertyId ?? (shouldRestoreCurrentData ? currentState?.propertyId ?? null : null),
    );
    this.selectedStands.set(
      previousStepData?.selectedStands ?? (shouldRestoreCurrentData ? currentState?.selectedStands ?? [] : []),
    );
    this.referenceSpecies.set(
      previousStepData?.referenceSpecies ??
        (shouldRestoreCurrentData ? currentState?.fusedStand?.species ?? null : null),
    );

    if (!shouldRestoreCurrentData && currentState) {
      context.patchStepState(activeStepId, {
        valid: false,
        completed: false,
        data: undefined,
        summary: [],
      });
    }

    this.selectedStandId.set(shouldRestoreCurrentData ? currentState?.fusedStand?.id ?? null : null);
    this.selectedStandDetail.set(
      shouldRestoreCurrentData ? currentState?.fusedStandDetail ?? null : null,
    );

    queueMicrotask(() => {
      this.standSelect()?.writeValue(this.selectedStandId());
    });
  }

  async onStandChange(value: SelectValue): Promise<void> {
    const standId = this.toSingleSelectValue(value);
    if (!standId) {
      this.clearSelection();
      return;
    }

    const input = this.buildStandValidationInput(standId);
    if (!input) {
      this.clearSelection();
      queueMicrotask(() => {
        this.standSelect()?.writeValue(null);
      });
      return;
    }

    this.loading.set(true);

    try {
      const detail = await firstValueFrom(this.standDivisionAndFusionService.validateFusedStand(input));
      this.selectedStandId.set(detail?.id !== undefined && detail?.id !== null ? String(detail.id) : null);
      this.selectedStandDetail.set(detail ?? null);

      if (detail?.id === undefined || detail?.id === null) {
        queueMicrotask(() => {
          this.standSelect()?.writeValue(null);
        });
      }
    } catch (error) {
      this.clearSelection();
      this.handleValidationError(error);
      queueMicrotask(() => {
        this.standSelect()?.writeValue(null);
      });
    } finally {
      this.loading.set(false);
    }
  }

  createNewStand(): void {
    const modalRef = this.modalBuilder.openModal(StandCreationForm, WRAPPER_CONFIG);

    modalRef.closed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.refreshStandOptions();
    });
  }

  refreshStandOptions(): void {
    this.refreshVersion.update(value => value + 1);

    queueMicrotask(() => {
      this.standSelect()?.writeValue(this.selectedStandId());
    });
  }

  async canLeave(): Promise<boolean> {
    const input = this.buildAdvanceValidationInput();
    if (!input) {
      this.notifyError('SelectionRequired');
      return false;
    }

    try {
      await firstValueFrom(this.standDivisionAndFusionService.ensureFusedStandCanAdvance(input));
      return true;
    } catch (error) {
      this.handleValidationError(error);
      return false;
    }
  }

  canLeaveSync(): boolean {
    return !this.loading();
  }

  collect(context: IWizardContext): StepSelectFusedStandData {
    const data: StepSelectFusedStandData = {
      sourceStep2Snapshot: this.buildStep2Snapshot({
        propertyId: this.propertyId(),
        propertyName: null,
        selectedStands: this.selectedStands(),
        totalCurrentArea: 0,
        totalNewArea: 0,
        totalSectionedArea: 0,
        referenceSpecies: this.referenceSpecies(),
        referenceLandContract: this.referenceRow()?.landContract ?? null,
        referenceForestContract: this.referenceRow()?.forestContract ?? null,
      }),
      propertyId: this.propertyId(),
      selectedStands: this.selectedStands(),
      fusedStand: this.fusedStand(),
      fusedStandDetail: this.selectedStandDetail(),
    };

    persistWizardStepData(context, {
      visited: true,
      valid: !!this.selectedStandDetail(),
      completed: true,
      data,
      dirty: true,
      meta: buildWizardStepDependencyMeta(data),
      invalidateStepIds: [5],
    });

    return data;
  }

  displayValue(value: string | null | undefined): string {
    return value?.trim() ? value : EMPTY_VALUE;
  }

  formatArea(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return EMPTY_VALUE;
    }

    return value.toFixed(2).replace('.', ',');
  }

  private referenceRow(): IDividedStandRow | null {
    return this.selectedStands()[0] ?? null;
  }

  private buildStandValidationInput(
    standIdValue: string,
  ): StandDivisionAndFusionFusedStandValidationInputDto | null {
    const propertyId = this.numericPropertyId();
    const standId = this.toPositiveInteger(standIdValue);

    if (!propertyId || !standId) {
      return null;
    }

    return {
      propertyId,
      standId,
      selectedStandIds: this.selectedStands()
        .map(stand => this.toPositiveInteger(stand.id))
        .filter((selectedStandId): selectedStandId is number => !!selectedStandId),
    };
  }

  private buildAdvanceValidationInput(): StandDivisionAndFusionFusedStandAdvanceValidationInputDto | null {
    const propertyId = this.numericPropertyId();
    const standId = this.toPositiveInteger(this.selectedStandId());

    if (!propertyId || !standId || !this.selectedStandDetail()) {
      return null;
    }

    return {
      propertyId,
      standId,
      selectedStandIds: this.selectedStands()
        .map(stand => this.toPositiveInteger(stand.id))
        .filter((selectedStandId): selectedStandId is number => !!selectedStandId),
    };
  }

  private toFusedStand(
    detail: StandDivisionAndFusionFusedStandDetailDto | null,
  ): DivisionFusionStand | null {
    if (detail?.id === undefined || detail.id === null) {
      return null;
    }

    const referenceRow = this.referenceRow();
    const referenceCycleRotation = referenceRow?.cycleRotation;

    return {
      id: String(detail.id),
      name: detail.name ?? String(detail.id),
      establishmentId: String(detail.propertyId ?? this.propertyId() ?? ''),
      currentArea: detail.standArea ?? 0,
      cycle: this.formatNumericValue(detail.cycleValue),
      rotation: this.formatNumericValue(detail.rotationValue),
      species: detail.plantation?.speciesName ?? referenceRow?.species ?? '',
      landContract:
        referenceRow?.landContract ??
        referenceCycleRotation?.landContractNumber ??
        detail.cycleRotation?.landContractNumber ??
        '',
      forestContract:
        referenceRow?.forestContract ??
        referenceCycleRotation?.fomentContractNumber ??
        detail.cycleRotation?.fomentContractNumber ??
        '',
      landContractType:
        this.readStringProperty(referenceCycleRotation, 'landContractTypeName') ??
        detail.cycleRotation?.landContractTypeName ??
        '',
      forestContractType:
        this.readStringProperty(referenceCycleRotation, 'fomentContractTypeName') ??
        detail.cycleRotation?.fomentContractTypeName ??
        '',
      propertyType:
        referenceCycleRotation?.propertyTypeName ?? detail.cycleRotation?.propertyTypeName ?? '',
      activeHarvestOrderId: null,
    };
  }

  private buildStep2Snapshot(value?: ISelectStandsToDivideStepData): string {
    return JSON.stringify({
      propertyId: value?.propertyId ?? null,
      referenceSpecies: value?.referenceSpecies ?? null,
      referenceLandContract: value?.referenceLandContract ?? null,
      referenceForestContract: value?.referenceForestContract ?? null,
      selectedStands: (value?.selectedStands ?? []).map(stand => ({
        id: Number(stand.id),
        currentArea: this.roundArea(stand.currentArea),
        newArea: this.roundArea(stand.newArea),
        sectionedArea: this.roundArea(stand.sectionedArea),
      })),
    });
  }

  private clearSelection(): void {
    this.selectedStandId.set(null);
    this.selectedStandDetail.set(null);
  }

  private numericPropertyId(): number | null {
    return this.toPositiveInteger(this.propertyId());
  }

  private toSingleSelectValue(value: SelectValue): string | null {
    if (Array.isArray(value)) {
      return value[0] ?? null;
    }

    return value || null;
  }

  private formatNumericValue(value: number | null | undefined): string | null {
    if (value === null || value === undefined) {
      return null;
    }

    return String(value);
  }

  private handleValidationError(error: unknown): void {
    if (isHandledByZnErrorHandler(error)) {
      return;
    }

    const message = extractBackendErrorMessage(error);
    if (!message) {
      return;
    }

    this.toaster.error(message, '', { life: DEFAULT_TOASTER_LIFE });
  }

  private notifyError(key: string): void {
    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::StandDivisionAndFusion.StepSelectFusedStand.${key}`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
  }


  private toPositiveInteger(value: number | string | null | undefined): number | null {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  private roundArea(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private readStringProperty(value: unknown, propertyName: string): string | null {
    const propertyValue = this.asRecord(value)?.[propertyName];
    return typeof propertyValue === 'string' && propertyValue.trim() ? propertyValue : null;
  }

  private asRecord(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : null;
  }
}

